import { CircularProgress } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import FormField from "components/FormField/FormField";
import { SelectOptions } from "components/Shared/CustomSelect/CustomSelect";
import React, { useEffect, useMemo, useState } from "react";
import { ControllerRenderProps } from "react-hook-form";

const AutoComplete: React.FC<{
  field: ControllerRenderProps<any, any>;
  error: string | null | object;
  options: SelectOptions;
  loading: boolean;
  label?: string;
  placeholder?: string;
  variant?: string;
  search?: (v: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onEndReached?: () => void;
  disabled?: boolean;
  getOptionDisabled?: (option: SelectOptions[number]) => boolean;
  multiple?: boolean;
  disableCloseOnSelect?: boolean;
  disableClearable?: boolean;
  disablePortal?: boolean;
}> = ({
  disabled,
  field,
  error,
  options,
  loading,
  label,
  search,
  onOpen,
  onClose,
  onEndReached,
  placeholder,
  variant = "standard",
  getOptionDisabled,
  multiple = false,
  disableCloseOnSelect = false,
  disableClearable = false,
  disablePortal = false,
}) => {
  const selectedValue = useMemo(() => {
    if (multiple) {
      return (field.value || []).map(
        (val: any) => options?.find((opt) => opt.value === val) || { value: val, label: val }
      );
    }
    if (!field.value) {
      return null;
    }

    const matchedOption = options?.find((opt) => opt.value === field.value);
    return matchedOption || { value: field.value, label: "" };
  }, [field.value, multiple, options]);

  const [inputValue, setInputValue] = useState<string>(() => {
    if (multiple) return "";
    if (!selectedValue) return "";
    return typeof selectedValue === "string" ? selectedValue : selectedValue?.label ?? "";
  });

  useEffect(() => {
    if (multiple) return;
    if (!selectedValue) {
      setInputValue("");
      return;
    }
    setInputValue(typeof selectedValue === "string" ? selectedValue : selectedValue?.label ?? "");
  }, [multiple, selectedValue]);

  const extraProps = {
    ...(search ? { filterOptions: (x) => x } : {}),
    ...(options?.every((x) => !!x?.category)
      ? {
          groupBy: (option: any) => option.category,
        }
      : {}),
  };

  return (
    <Autocomplete
      {...field}
      {...extraProps}
      multiple={multiple}
      disableCloseOnSelect={disableCloseOnSelect}
      disableClearable={disableClearable}
      disablePortal={disablePortal}
      loading={loading}
      options={options ?? []}
      inputValue={inputValue}
      openOnFocus
      onOpen={onOpen}
      onClose={onClose}
      filterSelectedOptions={multiple}
      ListboxProps={
        onEndReached
          ? {
              onScroll: (event) => {
                const listbox = event.currentTarget;
                if (listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 32) {
                  onEndReached();
                }
              },
            }
          : undefined
      }
      onInputChange={(event, newInputValue, reason) => {
        if (reason === "input") {
          setInputValue(newInputValue ?? "");
          if (search) {
            search(newInputValue ?? "");
          }
          return;
        }

        if (reason === "clear") {
          setInputValue("");
          if (search) {
            search("");
          }
          return;
        }

        if (reason === "reset") {
          // Ignore MUI internal resets so search typing is not overwritten on rerender.
          return;
        }

        setInputValue(newInputValue ?? "");
        if (search) {
          search(newInputValue ?? "");
        }
      }}
      onChange={(e, value) => {
        if (multiple) {
          const selectedValues = Array.isArray(value)
            ? value.map((item: any) => item?.value || item).filter(Boolean)
            : [];
          field.onChange(selectedValues);
        } else {
          const selectedOption = value as any;
          setInputValue(selectedOption?.label ?? "");
          field.onChange(selectedOption?.value || null);
        }
      }}
      renderOption={(props, option) => {
        return (
          <li {...props} key={`${option.label}-${option.value}`}>
            {option.label ?? ""}
          </li>
        );
      }}
      getOptionLabel={(option) => {
        if (option === "" || option === null || option === undefined) {
          return "";
        }
        if (typeof option === "string") {
          return options?.find((opt) => opt.value === option)?.label ?? "";
        }
        return option.label || "";
      }}
      isOptionEqualToValue={(option, value) => {
        if (typeof value === "string") {
          return option.value === value;
        }
        return option.value === value?.value;
      }}
      value={selectedValue}
      getOptionDisabled={getOptionDisabled}
      renderInput={(params) => (
        <FormField
          {...params}
          disabled={disabled}
          inputProps={{
            ...params.inputProps,
            disabled,
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
          onChange={(e) => {
            params.inputProps.onChange(e);
          }}
          label={label}
          variant={variant}
          error={error}
          placeholder={placeholder}
        />
      )}
    />
  );
};

export default AutoComplete;
